import React, { useState, useMemo, useEffect } from "react";
import {
  Box, Button, TextField, Stack, Typography,
  Accordion, AccordionSummary, AccordionDetails,
  Chip, Divider, Paper, List, ListItem, ListItemText, FormControlLabel, Checkbox,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/DisabledByDefault";

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: {"Content-Type":"application/json"},
    body: JSON.stringify(body || {})
  });
  return await res.json();
}

export default function PlannerTriagerPanel({ moduleName, onClose, onPatchPlanReady }) {
  const [module, setModule] = useState(moduleName || "template_generator");
  const [message, setMessage] = useState("");
  const [useGpt, setUseGpt] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [selectedPaths, setSelectedPaths] = useState([]); // <- selected targets for planner

  const tri = result?.triager_output;
  const patchPlan = result?.patch_plan;
  const applyCmds = useMemo(() => (patchPlan?.apply_commands || []), [patchPlan]);
  const allTargets = useMemo(() => (patchPlan?.targets || []), [patchPlan]);

  // helper to notify parent whenever selection/message changes
  const notifyParent = (paths = selectedPaths, msg = message) => {
    if (onPatchPlanReady) onPatchPlanReady(paths, msg);
  };

  const runTriage = async () => {
    setErr("");
    setResult(null);
    const r = await postJson("/api/planner/triage", { module, message, use_gpt: useGpt });
    if (!r.ok) { setErr((r.errors && r.errors[0]) || "Unknown error"); return; }
    setResult(r.data);
    // default: select all targets
    const defaults = (r?.data?.patch_plan?.targets || []).map(t => t.path);
    setSelectedPaths(defaults);
    notifyParent(defaults, message);
  };

  const saveSnapshot = async () => {
    if (!result) return;
    const r = await postJson("/api/planner/triage/snapshot", { module, result_json: result });
    if (!r.ok) { setErr((r.errors && r.errors[0]) || "Snapshot failed"); return; }
  };

  // propagate message changes upward
  useEffect(() => { notifyParent(selectedPaths, message); /* eslint-disable-next-line */ }, [message]);

  const llmUsed = !!result?.llm_used;

  const renderFileSecondary = (f) => {
    const base = [`type=${f.file_type}`, `kind=${f.kind}`, `score=${f.score}`];
    if (f.msg_hits?.length) base.push(`msg_hits=${f.msg_hits.join('|')}`);
    if (typeof f.llm_delta === "number") base.push(`llm_delta=${f.llm_delta}`);
    if (f.llm_why) base.push(`why=${f.llm_why}`);
    return base.join(" • ");
  };

  const togglePath = (p) => {
    setSelectedPaths(prev => {
      const next = prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p];
      notifyParent(next, message);
      return next;
    });
  };
  const selectAll = () => {
    const next = allTargets.map(t => t.path);
    setSelectedPaths(next);
    notifyParent(next, message);
  };
  const selectNone = () => {
    setSelectedPaths([]);
    notifyParent([], message);
  };

  return (
    <Box sx={{ p:2, border: "1px solid #e0e0e0", borderRadius: 2, mt: 2 }}>
      {/* Step 1: Inputs */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flex:1 }}>Planner Triager</Typography>
        {onClose && <Button size="small" onClick={onClose}>Close</Button>}
      </Stack>

      <Paper variant="outlined" sx={{ p:2, mb:2 }}>
        <Typography variant="subtitle2" sx={{ mb:1 }}>Step 1 — Inputs</Typography>
        <Stack direction={{xs:"column", md:"row"}} spacing={2} alignItems={{xs:"stretch", md:"center"}}>
          <TextField size="small" label="Module" value={module} onChange={e => setModule(e.target.value)} sx={{ minWidth: 220 }} />
          <TextField
            size="small"
            label="Message (optional)"
            value={message}
            onChange={e => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
          />
          <FormControlLabel
            control={<Checkbox checked={useGpt} onChange={e => setUseGpt(e.target.checked)} />}
            label="Use GPT assist"
          />
          <Button variant="contained" onClick={runTriage}>Run Triage</Button>
          <Button variant="outlined" onClick={saveSnapshot} disabled={!result}>Save Snapshot</Button>
        </Stack>
        {err && <Typography color="error" sx={{ mt:1 }}>{err}</Typography>}
        <Typography variant="caption" sx={{ mt:1, display:"block" }}>
          {result?.input?.message_keywords?.length
            ? `Keywords from message: ${result.input.message_keywords.join(", ")}`
            : " "}
          {llmUsed ? " • GPT assisted" : ""}
        </Typography>
      </Paper>

      {/* Step 2: Memory & Buckets */}
      {result && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p:2 }}>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Step 2 — Memory Bundle</Typography>
            <Divider sx={{ my:1 }}/>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>App Rules</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {(result.memory_bundle.app_rules || []).map((r,i)=>(
                    <ListItem key={i}><ListItemText primary={`• ${r}`} /></ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Module Facts</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {(result.memory_bundle.module_facts || []).map((r,i)=>(
                    <ListItem key={i}><ListItemText primary={`• ${r}`} /></ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>DB/API Cheats</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {(result.memory_bundle.db_api_cheats || []).map((r,i)=>(
                    <ListItem key={i}><ListItemText primary={`• ${r}`} /></ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Paper>

          <Paper variant="outlined" sx={{ p:2 }}>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Step 3 — Triager Output</Typography>
            <Divider sx={{ my:1 }}/>
            <Stack direction={{xs:"column", md:"row"}} spacing={2}>
              <Box sx={{ flex:1 }}>
                <Typography variant="subtitle1">Must Touch</Typography>
                <List dense>
                  {(tri?.must_touch || []).map((f,i)=>(
                    <ListItem key={i}>
                      <ListItemText primary={f.path} secondary={renderFileSecondary(f)} />
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Box sx={{ flex:1 }}>
                <Typography variant="subtitle1">Likely Touch</Typography>
                <List dense>
                  {(tri?.likely_touch || []).map((f,i)=>(
                    <ListItem key={i}>
                      <ListItemText primary={f.path} secondary={renderFileSecondary(f)} />
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Box sx={{ flex:1 }}>
                <Typography variant="subtitle1">Maybe Touch</Typography>
                <List dense>
                  {(tri?.maybe_touch || []).map((f,i)=>(
                    <ListItem key={i}>
                      <ListItemText primary={f.path} secondary={renderFileSecondary(f)} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Stack>
          </Paper>

          {/* Step 4: Patch Plan (with selection) */}
          <Paper variant="outlined" sx={{ p:2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Step 4 — Patch Plan Preview</Typography>
              <Box>
                <IconButton size="small" title="Select all" onClick={selectAll}><SelectAllIcon fontSize="small" /></IconButton>
                <IconButton size="small" title="Select none" onClick={selectNone}><DeselectIcon fontSize="small" /></IconButton>
              </Box>
            </Stack>
            <Divider sx={{ my:1 }}/>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Safe Apply Order</Typography>
            <Stack direction="row" spacing={1} sx={{ mb:2, flexWrap:"wrap" }}>
              {(result.apply_order || []).map((k,i)=>(
                <Chip key={i} label={k} />
              ))}
            </Stack>
            <Typography variant="subtitle2">Targets (check to include in Planner)</Typography>
            <List dense>
              {allTargets.map((t,i)=>(
                <ListItem
                  key={i}
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      checked={selectedPaths.includes(t.path)}
                      onChange={()=>togglePath(t.path)}
                    />
                  }
                >
                  <ListItemText primary={t.path} secondary={`kind=${t.kind}`} />
                </ListItem>
              ))}
            </List>
            <Typography variant="subtitle2" sx={{ mt:2 }}>Apply Commands</Typography>
            <Box component="pre" sx={{ p:1, bgcolor:"#f6f8fa", overflow:"auto" }}>
{applyCmds.join("\n")}
            </Box>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
